// src/components/ProductManagement/CreateProductForm.js
import React, { useEffect, useState } from 'react';
import config from '../../config';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
let CATEGORY_CACHE = null;

const RequiredStar = () => (
  <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>
);

const PricingNote = () => (
  <div
    style={{
      background: '#f0f9ff',
      border: '1px solid #bae6fd',
      color: '#0369a1',
      padding: '10px 12px',
      borderRadius: 8,
      fontSize: '0.85rem',
      marginBottom: 14
    }}
  >
    💡 <strong>Pricing Note:</strong> Offer price is automatically rounded to the
    nearest rupee.
    <br />
    Example: ₹399.20 → ₹399, ₹399.60 → ₹400.
  </div>
);


/* ===========================
   IMAGE NORMALIZATION HELPERS
   =========================== */

const IMAGE_CONFIG = {
  aspectRatio: 3 / 4,      // width / height (3:4 portrait)
  maxWidth: 1200,          // we will scale down if larger
  recommendedMinWidth: 900,
  recommendedMinHeight: 1200,
  hardMin: 400,            // below this is considered unusable
  quality: 0.85
};

// Helper: load an image element from a File
function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Helper: generate warnings based on original resolution & aspect ratio
function analyzeImageResolution(width, height) {
  const warnings = [];
  const {
    recommendedMinWidth,
    recommendedMinHeight,
    aspectRatio,
    hardMin
  } = IMAGE_CONFIG;

  if (width < hardMin || height < hardMin) {
    warnings.push(
      `Image is extremely small (${width}×${height}). It may look very blurry on mobile.`
    );
    return warnings;
  }

  if (width < recommendedMinWidth || height < recommendedMinHeight) {
    warnings.push(
      `Low resolution (${width}×${height}). Recommended minimum is ` +
      `${recommendedMinWidth}×${recommendedMinHeight} for best clarity.`
    );
  }

  const ratio = width / height;
  const diff = Math.abs(ratio - aspectRatio);

  if (diff > 0.2) {
    if (ratio > aspectRatio) {
      warnings.push(
        'Image is quite wide. Top/bottom parts may be cropped in mobile view.'
      );
    } else {
      warnings.push(
        'Image is quite tall. Sides may be cropped in mobile view.'
      );
    }
  }

  return warnings;
}

/**
 * Phase 1 strategy:
 * - Always output a 3:4 portrait image (full-bleed, Myntra-style).
 * - For wide/square originals -> crop from TOP to preserve head/shoulders.
 * - For taller images -> crop around vertical centre with a small upward bias.
 */
async function normalizeImageFile(file) {
  const img = await loadImageFromFile(file);

  const {
    aspectRatio,
    maxWidth,
    quality,
    hardMin
  } = IMAGE_CONFIG;

  const originalWidth = img.naturalWidth;
  const originalHeight = img.naturalHeight;

  // HARD BLOCK only if totally unusable
  if (originalWidth < hardMin || originalHeight < hardMin) {
    throw new Error(
      `Image is too small (${originalWidth}×${originalHeight}). ` +
      `Please upload a higher resolution image.`
    );
  }

  // Step 1: decide target dimensions with 3:4 ratio
  let targetWidth = Math.min(originalWidth, maxWidth);
  let targetHeight = Math.round(targetWidth / aspectRatio);

  if (targetHeight > originalHeight) {
    targetHeight = originalHeight;
    targetWidth = Math.round(targetHeight * aspectRatio);
  }

  // Step 2: smart crop from original image
  const cropWidth = targetWidth;
  const cropHeight = targetHeight;

  let cropX = Math.round((originalWidth - cropWidth) / 2);
  let cropY;
  const ratio = originalWidth / originalHeight;

  if (ratio >= aspectRatio) {
    // ✅ Wide or square image:
    // Keep TOP of the image to avoid chopping the head.
    cropY = 0;
  } else {
    // ✅ Tall image:
    // Centre vertically with a small upward bias.
    cropY = Math.round((originalHeight - cropHeight) / 2);

    const upwardBias = Math.round(cropHeight * 0.05); // 5% upwards
    cropY = Math.max(0, cropY - upwardBias);
    if (cropY + cropHeight > originalHeight) {
      cropY = originalHeight - cropHeight;
    }
  }

  // Safety clamp
  if (cropX < 0) cropX = 0;
  if (cropX + cropWidth > originalWidth) {
    cropX = originalWidth - cropWidth;
  }
  if (cropY < 0) cropY = 0;
  if (cropY + cropHeight > originalHeight) {
    cropY = originalHeight - cropHeight;
  }

  // Step 3: draw onto canvas
  const canvas = document.createElement('canvas');
  canvas.width = cropWidth;
  canvas.height = cropHeight;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    img,
    cropX, cropY, cropWidth, cropHeight, // source rect
    0, 0, cropWidth, cropHeight          // destination rect
  );

  // Step 4: canvas -> Blob (JPEG)
  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality)
  );

  const normalizedFile = new File(
    [blob],
    file.name.replace(/\.(png|jpeg|jpg|webp)$/i, '') + '_aurakart.jpg',
    { type: 'image/jpeg' }
  );

  return {
    normalizedFile,
    normalizedWidth: cropWidth,
    normalizedHeight: cropHeight,
    originalWidth,
    originalHeight
  };
}

export default function CreateProductForm({ onClose, onSuccess }) {
  const [currentStep, setCurrentStep] = useState(1);

  // --- Vendors (optional) ---
  const [vendors, setVendors] = useState([]);

  // --- Sellers (mandatory for admin) ---
  const [sellers, setSellers] = useState([]);
  const [selectedSellerId, setSelectedSellerId] = useState('');

  // --- Categories & Subcategories from backend ---
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [subCategories, setSubCategories] = useState([]);  // [{subCategoryId, subCategoryName, ...}]

  // --- Sizes (DB-driven) ---
  const [allSizes, setAllSizes] = useState([]);             // all sizes from /api/sizes
  const [availableSizes, setAvailableSizes] = useState([]); // sizes allowed for selected category+subCategory
  const [sizesLoading, setSizesLoading] = useState(false);
  const [sizesError, setSizesError] = useState('');

  // Global color list for product/variants
  const colorOptions = [
    "Black", "White", "Grey", "Navy Blue", "Blue", "Red", "Maroon", "Pink",
    "Green", "Yellow", "Mustard", "Orange", "Purple", "Brown", "Beige", "Off-White",
    // then the rest...
    "Charcoal", "Cream", "Ivory", "Sky Blue", "Aqua", "Teal", "Turquoise", "Olive",
    "Mint Green", "Pista Green", "Bottle Green", "Sage Green", "Wine", "Burgundy",
    "Coral", "Blush Pink", "Dusty Pink", "Rose", "Hot Pink", "Rust", "Terracotta",
    "Lavender", "Chocolate Brown", "Tan", "Khaki", "Camel", "Multicolor", "Printed"
  ];

  const [form, setForm] = useState({
    productName: '',
    productDescription: '',
    categoryName: '',
    subCategoryName: '',
    vendorId: '',
    hsnCode: '',
    active: true,

    details: {
      fabric: '',
      pattern: '',
      neck: '',
      sleeveLength: '',
      fit: '',
      occasion: '',
      brand: '',
      countryOfOrigin: 'India',
      material: '',
      careInstructions: '',
      features: '',
      neckline: '',
      sleeves: '',
      season: '',
      closure: '',
      manufacturer: ''
    },

    variants: [{
      sku: '',
      sizeName: '',
      color: '',
      originalPrice: '',
      discount: '',
      offerPrice: '',
      stockQuantity: '',
      images: []
    }],

    images: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [productId, setProductId] = useState(null);

  // Colors stored for this product in DB (/api/products/{id}/colors)
  const [productColors, setProductColors] = useState([]);
  const [selectedImageColor, setSelectedImageColor] = useState('');
  const [completedColors, setCompletedColors] = useState([]); // colors for which images uploaded in this session

  // Each entry: null or { file, originalWidth, originalHeight, normalizedWidth, normalizedHeight, warnings[] }
  const [imageViewTypes, setImageViewTypes] = useState({
    'Main View': null,
    'Front View': null,
    'Back View': null,
    'Left Side View': null,
    'Right Side View': null
  });

  // ---------------- FETCHERS ----------------

  // Fetch vendors (optional)
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await fetch(`${config.baseUrl}/api/vendors`);
        if (response.ok) {
          const data = await response.json();
          setVendors(data);
          if (data.length > 0) {
            setForm(prev => ({ ...prev, vendorId: data[0].vendorId }));
          }
        }
      } catch (err) {
        console.error('Failed to fetch vendors:', err);
      }
    };
    fetchVendors();
  }, []);

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/api/admin/sellers/approved`);
        if (res.ok) {
          const data = await res.json();
          setSellers(data || []);
          if (data.length > 0) {
            setSelectedSellerId(data[0].id); // default select first seller
          }
        }
      } catch (err) {
        console.error('Failed to fetch sellers:', err);
      }
    };
    fetchSellers();
  }, []);

  // Fetch all categories from backend
  useEffect(() => {
    const fetchCategories = async () => {

      // ✅ Use cache if already loaded
      if (CATEGORY_CACHE) {
        setCategories(CATEGORY_CACHE);
        setCategoriesLoading(false);
        return;
      }

      try {
        setCategoriesLoading(true);

        const res = await fetch(`${config.baseUrl}/api/categories`);

        if (res.ok) {
          const data = await res.json();

          CATEGORY_CACHE = data; // ✅ save to cache
          setCategories(data || []);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // When categoryName changes, reset subCategoryName and fetch subcategories from backend
  useEffect(() => {
    if (!form.categoryName) {
      setSubCategories([]);
      return;
    }

    const fetchSubCategories = async () => {
      try {
        const encoded = encodeURIComponent(form.categoryName);
        const res = await fetch(`${config.baseUrl}/api/subCategories/category/${encoded}`);
        if (res.ok) {
          const data = await res.json();
          setSubCategories(data || []);
        } else {
          setSubCategories([]);
        }
      } catch (err) {
        console.error('Failed to fetch subcategories:', err);
        setSubCategories([]);
      }
    };

    setForm(prev => ({ ...prev, subCategoryName: '' }));
    fetchSubCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.categoryName]);

  // Fetch all sizes (for fallback)
  useEffect(() => {
    const fetchAllSizes = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/api/sizes`);
        if (res.ok) {
          let data = await res.json();
          data = (data || []).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
          setAllSizes(data);
        }
      } catch (err) {
        console.error('Failed to fetch sizes:', err);
      }
    };
    fetchAllSizes();
  }, []);

  // Fetch sizes for selected category + subcategory (from sub_category_sizes)
  useEffect(() => {
    if (!form.categoryName || !form.subCategoryName) {
      setAvailableSizes([]);
      setSizesError('');
      return;
    }

    const fetchSizesForSubCategory = async () => {
      setSizesLoading(true);
      setSizesError('');
      try {
        const params = new URLSearchParams({
          categoryName: form.categoryName,
          subCategoryName: form.subCategoryName
        });
        const res = await fetch(`${config.baseUrl}/api/subCategorySizes?${params.toString()}`);
        if (res.ok) {
          let data = await res.json();
          data = (data || []).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
          setAvailableSizes(data);
          if (!data || data.length === 0) {
            setSizesError('No specific sizes configured for this sub-category. Showing all sizes as fallback.');
          }
        } else {
          setAvailableSizes([]);
          setSizesError('Unable to load sizes for this sub-category. Showing all sizes as fallback.');
        }
      } catch (err) {
        console.error('Failed to fetch subCategory sizes:', err);
        setAvailableSizes([]);
        setSizesError('Failed to load sub-category sizes. Showing all sizes as fallback.');
      } finally {
        setSizesLoading(false);
      }
    };

    setForm(prev => ({
      ...prev,
      variants: prev.variants.map(v => ({ ...v, sizeName: '' }))
    }));

    fetchSizesForSubCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.categoryName, form.subCategoryName]);

  // After product is created, fetch its colors for Step 4
  useEffect(() => {
    if (!productId) return;

    const fetchProductColors = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/api/products/${productId}/colors`);
        if (!res.ok) return;
        const data = await res.json();
        setProductColors(data || []);
        if (data && data.length > 0 && !selectedImageColor) {
          setSelectedImageColor(data[0].colorName);
        }
      } catch (err) {
        console.error('Failed to fetch product colors:', err);
      }
    };

    fetchProductColors();
  }, [productId, selectedImageColor]);

  // ---------------- BASIC HANDLERS ----------------

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleDetailsChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      details: { ...prev.details, [field]: value }
    }));
  };

  // -------- VARIANTS --------

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...form.variants];
    const updatedVariant = { ...newVariants[index], [field]: value };

    // 🔒 Prevent duplicate size + color ONLY when both exist
    if (
      (field === 'sizeName' || field === 'color') &&
      updatedVariant.sizeName &&
      updatedVariant.color
    ) {
      const exists = form.variants.some(
        (v, i) =>
          i !== index &&
          v.sizeName === updatedVariant.sizeName &&
          v.color === updatedVariant.color
      );

      if (exists) {
        alert('This size & color combination already exists.');
        return;
      }
    }

    // Auto-calculate offer price (ROUNDED)
    if (field === 'discount' || field === 'originalPrice') {
      const originalPrice = parseFloat(updatedVariant.originalPrice) || 0;
      const discount = parseFloat(updatedVariant.discount) || 0;

      const calculatedPrice = originalPrice * (1 - discount / 100);

      // ✅ Round to nearest rupee
      updatedVariant.offerPrice =
        originalPrice > 0
          ? Math.round(calculatedPrice)
          : '';
    }


    newVariants[index] = updatedVariant;
    setForm(prev => ({ ...prev, variants: newVariants }));
  };

  const addVariant = () => {
    setForm(prev => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          sku: '',
          sizeName: '',
          color: '',
          originalPrice: '',
          discount: '',
          offerPrice: '',
          stockQuantity: '',
          images: []
        }
      ]
    }));
  };

  const removeVariant = (index) => {
    if (form.variants.length <= 1) return;
    setForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const cloneVariant = (index) => {
    const source = form.variants[index];
    const cloned = {
      ...source,
      sku: '',
      sizeName: '',
      stockQuantity: ''
    };
    const newVariants = [...form.variants];
    newVariants.splice(index + 1, 0, cloned);
    setForm(prev => ({ ...prev, variants: newVariants }));
  };

  // -------- IMAGES (Step 4 local state) --------

  const handleImageUploadForView = async (viewType, file) => {
    if (!file) return;

    try {
      const {
        normalizedFile,
        normalizedWidth,
        normalizedHeight,
        originalWidth,
        originalHeight
      } = await normalizeImageFile(file);

      const warnings = analyzeImageResolution(originalWidth, originalHeight);

      setImageViewTypes(prev => ({
        ...prev,
        [viewType]: {
          file: normalizedFile,
          originalWidth,
          originalHeight,
          normalizedWidth,
          normalizedHeight,
          warnings
        }
      }));
    } catch (err) {
      console.error('Image processing failed:', err);
      setError(
        err.message ||
        'Failed to process image. Please try with a higher resolution image.'
      );
    }
  };

  const removeImageFromView = (viewType) => {
    setImageViewTypes(prev => ({
      ...prev,
      [viewType]: null
    }));
  };

  // ---------------- STEP 3: SAVE PRODUCT (NO IMAGES) ----------------

  const handleSaveProductAtStep3 = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 1) Basic mandatory fields
      if (!form.productName || !form.categoryName || !form.subCategoryName) {
        throw new Error(
          'Please fill all mandatory fields: Product Name, Category and Sub Category.'
        );
      }

      // ✅ ADD THIS BLOCK HERE
      if (!selectedSellerId) {
        throw new Error('Please select a Seller before creating the product.');
      }

      // 2) HSN is mandatory now
      if (!form.hsnCode) {
        throw new Error('Please enter HSN Code.');
      }

      // 3) Variant validation
      const filledVariants = form.variants.filter((v) =>
        v.sizeName &&
        v.color &&                     // ← added
        v.sku &&
        v.originalPrice !== '' &&
        v.stockQuantity !== ''
      );

      if (filledVariants.length === 0) {
        throw new Error(
          'Please add at least one complete variant with Size, Color, SKU, Original Price and Stock Quantity.'
        );
      }

      if (filledVariants.length !== form.variants.length) {
        throw new Error(
          'Please complete all variant fields (Size, Color, SKU, Original Price, Stock Quantity) or remove unfinished variants.'
        );
      }

      // 4) Build payload
      const payload = {
        productName: form.productName,
        productDescription: form.productDescription,
        categoryName: form.categoryName,
        subCategoryName: form.subCategoryName,
        sellerId: selectedSellerId ? parseInt(selectedSellerId, 10) : null, // ✅ ADD THIS
        active: form.active,
        hsnCode: form.hsnCode,
        details: form.details,
        variants: filledVariants.map((v) => ({
          sku: v.sku,
          sizeName: v.sizeName,
          color: v.color || null,
          originalPrice: parseFloat(v.originalPrice),
          offerPrice: Math.round(parseFloat(v.offerPrice || 0)),
          discount: parseFloat(v.discount || 0),
          stockQuantity: parseInt(v.stockQuantity || 0, 10),
          images: v.images || []
        })),
        images: []
      };

      // 5) Call backend
      const response = await fetch(`${config.baseUrl}/api/products/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([payload])
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create product');
      }

      const createdProducts = await response.json();
      const createdProduct = Array.isArray(createdProducts)
        ? createdProducts[0]
        : createdProducts;
      const newProductId = createdProduct.productId || createdProduct.id;

      if (!newProductId) {
        throw new Error('Product created but no product ID returned from server.');
      }

      // 6) Success → move to Step 4 (images)
      setProductId(newProductId);
      setSuccess('Product saved successfully! Now upload images for each color.');
      setCurrentStep(4);
    } catch (err) {
      setError(err.message || 'Something went wrong while saving the product.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------- STEP 4: SAVE IMAGES FOR SELECTED COLOR ----------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!productId) {
        throw new Error('Product ID not found. Please complete Step 3 first.');
      }

      if (productColors.length > 0 && !selectedImageColor) {
        throw new Error('Please select a color for these images.');
      }

      const allImages = Object.entries(imageViewTypes)
        .filter(([_, info]) => info && info.file)
        .map(([viewType, info]) => ({ viewType, file: info.file }));

      if (allImages.length === 0) {
        throw new Error('Please upload at least one image for the selected color.');
      }

      const auth = getAuth();
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      const uploadedImages = await Promise.all(
        allImages.map(async ({ viewType, file }, idx) => {
          const userId = auth.currentUser.uid;
          const timestamp = Date.now();
          const filename = `image_${timestamp}_${idx}.jpg`;
          const storageRef = ref(storage, `product-images/${userId}/${productId}/${filename}`);

          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);

          return {
            imageUrl: url,
            imageType: viewType,
            displayOrder: idx + 1,
            color: selectedImageColor || null
          };
        })
      );

      const imageResponse = await fetch(
        `${config.baseUrl}/api/productimages/product/${productId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(uploadedImages)
        }
      );

      if (!imageResponse.ok) {
        const errorData = await imageResponse.json().catch(() => ({}));
        console.warn(
          'Warning: Images saved to Firebase but backend upload had issues:',
          errorData
        );
      }

      const label = selectedImageColor || 'Default';

      if (label && !completedColors.includes(label)) {
        setCompletedColors(prev => [...prev, label]);
      }

      setSuccess(
        `Images uploaded successfully for color: ${label}. ` +
        'You can switch to another color and upload again, or click "Finish Product & Close".'
      );

      setImageViewTypes({
        'Main View': null,
        'Front View': null,
        'Back View': null,
        'Left Side View': null,
        'Right Side View': null
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const goToNextStep = () => setCurrentStep(prev => Math.min(prev + 1, 4));
  const goToPrevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleFinish = () => {
    onSuccess && onSuccess();
    onClose && onClose();
  };

  // Helper: which size list to use in dropdown
  const sizeOptions = availableSizes.length > 0 ? availableSizes : allSizes;

  return (
    <div className="modal-overlay">
      <form className="modal-content large-modal" onSubmit={handleSubmit}>
        <div className="modal-header">
          <h2>Create New Product</h2>
          <div className="step-indicator">
            {[1, 2, 3, 4].map(step => (
              <span
                key={step}
                className={
                  currentStep === step
                    ? 'active'
                    : currentStep > step
                      ? 'completed'
                      : ''
                }
                onClick={() => setCurrentStep(step)}
                title={['Basic Info', 'Details', 'Variants', 'Images'][step - 1]}
              >
                {step}
              </span>
            ))}
          </div>
          <button type="button" className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {/* STEP 1: BASIC INFO */}
          {currentStep === 1 && (
            <div className="form-section">
              <h3>Step 1: Basic Product Information</h3>
              <p className="step-description">
                Fill basic details first. Product color is optional – you can also set colors per variant in Step 3.
              </p>
              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Product Name <RequiredStar />
                  </label>
                  <input
                    value={form.productName}
                    onChange={(e) => handleChange('productName', e.target.value)}
                    placeholder="Enter product name"
                  />
                </div>

                <div className="form-group">
                  <label>
                    Category <RequiredStar />
                  </label>
                  <select
                    value={form.categoryName}
                    onChange={(e) => handleChange('categoryName', e.target.value)}
                    disabled={categoriesLoading}
                  >
                    <option value="">
                      {categoriesLoading ? "Loading categories..." : "Select Category"}
                    </option>

                    {categories.map(cat => (
                      <option key={cat.categoryId} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>

                </div>

                <div className="form-group">
                  <label>
                    Sub Category <RequiredStar />
                  </label>
                  <select
                    value={form.subCategoryName}
                    onChange={(e) => handleChange('subCategoryName', e.target.value)}
                    disabled={!form.categoryName}
                  >
                    <option value="">Select Sub Category</option>
                    {subCategories.map(sc => (
                      <option key={sc.subCategoryId} value={sc.subCategoryName}>
                        {sc.subCategoryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    HSN Code <RequiredStar />
                  </label>
                  <input
                    value={form.hsnCode}
                    onChange={(e) => handleChange('hsnCode', e.target.value)}
                    placeholder="Enter HSN Code"
                  />
                  <small className="helper-text">
                    Example: 6109 for T-Shirts, 6204 for dresses. Check with your CA if unsure.
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    Seller <RequiredStar />
                  </label>
                  <select
                    value={selectedSellerId}
                    onChange={(e) => setSelectedSellerId(e.target.value)}
                  >
                    <option value="">Select Seller</option>
                    {sellers.map(seller => (
                      <option key={seller.id} value={seller.id}>
                        {seller.storeName || seller.businessName || seller.name}
                      </option>
                    ))}
                  </select>
                  <small className="helper-text">
                    Product will belong to this seller and appear in their dashboard.
                  </small>
                </div>

              </div>

              <div className="form-group full-width">
                <label>Product Description</label>
                <textarea
                  rows="4"
                  value={form.productDescription}
                  onChange={(e) => handleChange('productDescription', e.target.value)}
                  placeholder="Enter detailed product description"
                />
              </div>

              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => handleChange('active', e.target.checked)}
                  />
                  <span className="checkbox-text">
                    {form.active ? '✓ Product Active' : '○ Product Inactive (Pending Review)'}
                  </span>
                </label>
                <small className="helper-text">
                  Uncheck to mark as inactive – product can be reviewed and activated later.
                </small>
              </div>

              <div className="form-actions">
                <button type="button" onClick={onClose}>Cancel</button>
                <button type="button" onClick={goToNextStep} className="btn-primary">
                  Next: Additional Details
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: ADDITIONAL DETAILS */}
          {currentStep === 2 && (
            <div className="form-section">
              <h3>Step 2: Additional Product Details</h3>
              <p className="step-description">
                These fields help customers understand the fabric, fit and usage. They are optional but recommended.
              </p>
              <div className="form-grid">
                <div className="form-group">
                  <label>Fabric</label>
                  <input
                    value={form.details.fabric}
                    onChange={(e) => handleDetailsChange('fabric', e.target.value)}
                    placeholder="e.g., 100% Cotton"
                  />
                </div>

                <div className="form-group">
                  <label>Pattern</label>
                  <input
                    value={form.details.pattern}
                    onChange={(e) => handleDetailsChange('pattern', e.target.value)}
                    placeholder="e.g., Solid, Striped"
                  />
                </div>

                <div className="form-group">
                  <label>Neck Type</label>
                  <input
                    value={form.details.neck}
                    onChange={(e) => handleDetailsChange('neck', e.target.value)}
                    placeholder="e.g., Round, V-neck, Collar"
                  />
                </div>

                <div className="form-group">
                  <label>Sleeve Length</label>
                  <select
                    value={form.details.sleeveLength}
                    onChange={(e) => handleDetailsChange('sleeveLength', e.target.value)}
                  >
                    <option value="">Select Sleeve Length</option>
                    <option value="Sleeveless">Sleeveless</option>
                    <option value="Short Sleeve">Short Sleeve</option>
                    <option value="3/4 Sleeve">3/4 Sleeve</option>
                    <option value="Long Sleeve">Long Sleeve</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Fit</label>
                  <select
                    value={form.details.fit}
                    onChange={(e) => handleDetailsChange('fit', e.target.value)}
                  >
                    <option value="">Select Fit</option>
                    <option value="Loose">Loose</option>
                    <option value="Regular Fit">Regular Fit</option>
                    <option value="Slim Fit">Slim Fit</option>
                    <option value="Skinny">Skinny</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Occasion</label>
                  <input
                    value={form.details.occasion}
                    onChange={(e) => handleDetailsChange('occasion', e.target.value)}
                    placeholder="e.g., Casual, Formal, Party"
                  />
                </div>

                <div className="form-group">
                  <label>Brand</label>
                  <input
                    value={form.details.brand}
                    onChange={(e) => handleDetailsChange('brand', e.target.value)}
                    placeholder="Enter brand name"
                  />
                </div>

                <div className="form-group">
                  <label>Country of Origin</label>
                  <input
                    value={form.details.countryOfOrigin}
                    onChange={(e) => handleDetailsChange('countryOfOrigin', e.target.value)}
                    placeholder="Country"
                  />
                </div>

                <div className="form-group">
                  <label>Material</label>
                  <input
                    value={form.details.material}
                    onChange={(e) => handleDetailsChange('material', e.target.value)}
                    placeholder="e.g., Cotton, Polyester"
                  />
                </div>

                <div className="form-group">
                  <label>Care Instructions</label>
                  <input
                    value={form.details.careInstructions}
                    onChange={(e) => handleDetailsChange('careInstructions', e.target.value)}
                    placeholder="e.g., Machine wash cold"
                  />
                </div>

                <div className="form-group">
                  <label>Features</label>
                  <input
                    value={form.details.features}
                    onChange={(e) => handleDetailsChange('features', e.target.value)}
                    placeholder="e.g., Button closure, Pockets"
                  />
                </div>

                <div className="form-group">
                  <label>Season</label>
                  <input
                    value={form.details.season}
                    onChange={(e) => handleDetailsChange('season', e.target.value)}
                    placeholder="e.g., Spring, Summer, Winter"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>Back</button>
                <button type="button" onClick={goToNextStep} className="btn-primary">
                  Next: Variants & Pricing
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: VARIANTS */}
          {currentStep === 3 && (
            <div className="form-section">
              <h3>Step 3: Variants, Colors & Pricing</h3>
              <p className="step-description">
                Sizes are loaded based on Category and Sub Category using your sub_category_sizes table.
                Use the Clone button to quickly create similar variants.
              </p>
              <PricingNote />


              {sizesLoading && (
                <div className="error-message" style={{ background: '#dbeafe', color: '#1d4ed8', borderColor: '#bfdbfe' }}>
                  Loading sizes for this sub-category…
                </div>
              )}
              {sizesError && (
                <div className="error-message" style={{ marginBottom: 10 }}>
                  {sizesError}
                </div>
              )}

              <div className="variants-container">
                {form.variants.map((variant, idx) => (
                  <div key={idx} className="variant-card">
                    <div className="variant-header">
                      <h5>Variant {idx + 1}</h5>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          style={{
                            background: '#e5e7eb',
                            borderRadius: 999,
                            border: '1px solid #d1d5db',
                            padding: '4px 10px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                          onClick={() => cloneVariant(idx)}
                        >
                          Clone
                        </button>
                        {form.variants.length > 1 && (
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => removeVariant(idx)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="variant-grid">
                      <div className="form-group">
                        <label>
                          Size <RequiredStar />
                        </label>
                        <select
                          value={variant.sizeName}
                          onChange={(e) => handleVariantChange(idx, 'sizeName', e.target.value)}
                          disabled={sizeOptions.length === 0}
                        >
                          <option value="">Select Size</option>
                          {sizeOptions.map(size => (
                            <option key={size.sizeId} value={size.code}>
                              {size.code} {size.name ? `- ${size.name}` : ''}
                            </option>
                          ))}
                        </select>
                        <small className="helper-text">
                          Sizes follow your mapping for this Category &amp; Sub Category.
                        </small>
                      </div>

                      <div className="form-group">
                        <label>
                          Color <RequiredStar />
                        </label>
                        <select
                          value={variant.color || ''}
                          onChange={(e) => handleVariantChange(idx, 'color', e.target.value)}
                          required
                        >
                          <option value="">Select Color *</option>
                          {colorOptions.map(color => (
                            <option key={color} value={color}>
                              {color}
                            </option>
                          ))}
                        </select>
                        <small className="helper-text">
                          Required — used for filtering and display
                        </small>
                      </div>

                      <div className="form-group">
                        <label>
                          SKU <RequiredStar />
                        </label>
                        <input
                          value={variant.sku}
                          onChange={(e) => handleVariantChange(idx, 'sku', e.target.value)}
                          placeholder="Unique SKU code"
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          Original Price <RequiredStar />
                        </label>
                        <input
                          type="number"
                          step="1"
                          value={variant.originalPrice}
                          onChange={(e) => handleVariantChange(idx, 'originalPrice', e.target.value)}
                          placeholder="0.00"
                        />
                      </div>

                      <div className="form-group">
                        <label>Discount (%)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={variant.discount}
                          onChange={(e) => handleVariantChange(idx, 'discount', e.target.value)}
                          placeholder="0"
                        />
                      </div>

                      <div className="form-group">
                        <label>Offer Price (Auto Rounded)</label>
                        <input
                          type="number"
                          step="1"
                          value={variant.offerPrice}
                          readOnly
                          className="readonly-field"
                        />
                      </div>

                      <div className="form-group">
                        <label>
                          Stock Quantity <RequiredStar />
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={variant.stockQuantity}
                          onChange={(e) => handleVariantChange(idx, 'stockQuantity', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button type="button" className="btn-add-variant" onClick={addVariant}>
                  + Add Another Variant
                </button>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>Back</button>
                <button
                  type="button"
                  onClick={handleSaveProductAtStep3}
                  disabled={loading || !selectedSellerId}
                  className="btn-primary"
                >
                  {loading ? 'Saving Product...' : 'Save Product & Next'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: IMAGES PER COLOR */}
          {currentStep === 4 && (
            <div className="form-section">
              <h3>Step 4: Product Images</h3>
              <p className="step-description">
                Upload high-quality images for each color. We&apos;ll show you a preview of how it
                will look on mobile (3:4 portrait). If the resolution or ratio is not ideal,
                we&apos;ll warn you but won&apos;t stop you.
              </p>

              {productColors.length > 0 && (
                <div className="image-status-notice">
                  <strong>Note:</strong> Only product colors are listed below. Make sure you upload
                  at least one image for every color you created in Step 3.
                </div>
              )}

              {completedColors.length > 0 && (
                <div style={{ marginBottom: 12, fontSize: '0.85rem' }}>
                  <strong>Colors completed in this session:</strong>{' '}
                  {completedColors.map(c => (
                    <span
                      key={c}
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        marginRight: 6,
                        marginTop: 4,
                        borderRadius: 12,
                        border: '1px solid #d1d5db',
                        background: '#f3f4f6'
                      }}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}

              {productColors.length > 0 ? (
                <div className="form-group" style={{ maxWidth: 320, marginBottom: 18 }}>
                  <label>
                    Image Color <RequiredStar />
                  </label>
                  <select
                    value={selectedImageColor}
                    onChange={(e) => setSelectedImageColor(e.target.value)}
                  >
                    <option value="">Select Color</option>
                    {productColors.map(pc => (
                      <option key={pc.id} value={pc.colorName}>
                        {pc.colorName}
                      </option>
                    ))}
                  </select>
                  <small className="helper-text">
                    Only product colors are listed here. Upload images for one color at a time.
                  </small>
                </div>
              ) : (
                <div className="form-group" style={{ marginBottom: 18 }}>
                  <label>Image Color</label>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    No product-level colors found. Images will be saved without color grouping.
                  </p>
                </div>
              )}

              <div className="image-views-grid">
                {Object.keys(imageViewTypes).map(viewType => {
                  const info = imageViewTypes[viewType];
                  return (
                    <div key={viewType} className="image-view-card">
                      <h5>{viewType}</h5>
                      <label className="upload-label">
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={async (e) =>
                            await handleImageUploadForView(
                              viewType,
                              e.target.files && e.target.files[0]
                            )
                          }
                        />
                        <div className="upload-icon">📷</div>
                        <div className="upload-text">Click to upload</div>
                      </label>

                      {info && info.file && (
                        <div style={{ marginTop: 12, position: 'relative' }}>
                          <div className="image-mobile-preview-frame">
                            <img
                              src={URL.createObjectURL(info.file)}
                              alt={viewType}
                            />
                          </div>

                          <div className="image-resolution-info">
                            <div>
                              Original: {info.originalWidth}×{info.originalHeight}px
                            </div>
                            <div>
                              Displayed as: {info.normalizedWidth}×{info.normalizedHeight}px (3:4)
                            </div>

                            {info.warnings && info.warnings.length > 0 && (
                              <div className="image-warning-box">
                                <strong>Heads up:</strong>
                                <ul>
                                  {info.warnings.map((w, i) => (
                                    <li key={i}>{w}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            className="remove-image"
                            onClick={() => removeImageFromView(viewType)}
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleFinish}
                >
                  Finish Product &amp; Close
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Uploading Images...' : 'Save Images for Selected Color'}
                </button>
              </div>

              <p style={{ marginTop: 8, fontSize: '0.8rem', color: '#6b7280' }}>
                Tip: After you finish uploading images for all colors, click&nbsp;
                <strong>&quot;Finish Product &amp; Close&quot;</strong> to exit without creating
                duplicates.
              </p>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
